import React, { useCallback, useEffect, useState } from "react";
import { Button, Image, Spin } from "antd";
import { toast } from "react-toastify";

const getGifFileName = (url, fallback = "preview.gif") => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const name = path.split("/").filter(Boolean).pop();
    if (name) return name;
  } catch (e) {
    // fall through
  }
  return fallback;
};

const GifPreviewActions = ({ onPreview, onDownload, onEdit, showEdit }) => (
  <div className="gif-preview-actions">
    <Button className="greenBtn gif-preview-btn" onClick={onPreview}>
      Preview
    </Button>
    {showEdit && onEdit && (
      <Button className="greenBtn gif-preview-btn" onClick={onEdit}>
        Edit
      </Button>
    )}
    <Button className="greenBtn gif-preview-btn" onClick={onDownload}>
      Download GIF
    </Button>
  </div>
);

const GifResultPreview = ({
  url,
  fileName,
  loading = false,
  error = "",
  onEdit,
  showEdit = true,
  showFooterButtons = true,
  renderLayout,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("gif-preview-open", previewOpen);
    return () => document.body.classList.remove("gif-preview-open");
  }, [previewOpen]);

  const handleDownload = useCallback(async () => {
    if (!url) {
      toast.error("There is no GIF to download yet.");
      return;
    }

    const name = fileName || getGifFileName(url);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("download failed");
      const blob = await response.blob();
      if (!blob.size) throw new Error("empty");
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name.endsWith(".gif") ? name : `${name}.gif`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Unable to download the GIF. Please try again.");
    }
  }, [fileName, url]);

  const actions = (
    <GifPreviewActions
      onPreview={() => setPreviewOpen(true)}
      onDownload={handleDownload}
      onEdit={onEdit}
      showEdit={showEdit}
    />
  );

  const previewModal = url ? (
    <Image
      src={url}
      style={{ display: "none" }}
      preview={{
        visible: previewOpen,
        src: url,
        onVisibleChange: (visible) => setPreviewOpen(visible),
      }}
    />
  ) : null;

  if (loading) {
    const content = (
      <div className="gif-result-preview gif-result-preview--empty">
        <Spin tip="Generating GIF" size="large" />
      </div>
    );
    if (renderLayout) {
      return renderLayout({ image: content, actions: null, previewModal: null });
    }
    return content;
  }

  if (error) {
    const content = (
      <div className="gif-result-preview gif-result-preview--empty">
        <p className="gif-result-error">{error}</p>
      </div>
    );
    if (renderLayout) {
      return renderLayout({ image: content, actions: null, previewModal: null });
    }
    return content;
  }

  if (!url) {
    const content = (
      <div className="gif-result-preview gif-result-preview--empty">
        <p>Generated GIF preview will appear here.</p>
      </div>
    );
    if (renderLayout) {
      return renderLayout({ image: content, actions: null, previewModal: null });
    }
    return content;
  }

  const image = (
    <img
      src={url}
      alt="Generated GIF"
      className="c1 carousel-image"
      style={{ maxWidth: "100%", maxHeight: "100%" }}
    />
  );

  if (renderLayout) {
    return (
      <>
        {renderLayout({
          image,
          actions,
          previewModal,
        })}
      </>
    );
  }

  return (
    <div className="gif-result-preview">
      <div className="iframe-comp GIF">
        <div className="gif-preview-wrap">
          {image}
          {showFooterButtons ? actions : null}
          {previewModal}
        </div>
      </div>
    </div>
  );
};

export default GifResultPreview;
